/**
 * Sprint 006 (B1) — Tenant-scoped TanStack Query key factories for the
 * expense-era resources (services/api/*).
 *
 * Every tenant-scoped resource gets its tenantId at index 1 of the key:
 *   [resource, tenantId, ...rest]
 *
 * Why: query keys previously ignored the active tenant, so switching
 * tenants briefly showed the other tenant's cached rows (stale-flash;
 * `useSelectTenant` invalidates everything, so it self-healed after the
 * refetch). With the tenant in the key, tenant A's cache entries can
 * never be rendered while tenant B is active — and switching back to A
 * serves A's still-cached data instantly.
 *
 * Invalidation conventions:
 * - Scoped (preferred, from mutation hooks): pass the active tenant,
 *   e.g. `qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) })`.
 * - Cross-tenant (rare, e.g. tenant switch): the bare resource prefix
 *   still matches every tenant: `{ queryKey: ['expenses'] }`.
 *
 * Deliberately NOT tenant-scoped (cross-tenant admin surfaces):
 *   ['admin-audit-log'], ['admin-storage-status'], ['recognition-schemas'],
 *   ['session'].
 *
 * The 001-era hooks (useInvoices, useCustomers, …) keep their local
 * factories — same shape contract, enforced by keys.test.ts.
 */

/** `null` while the session is loading / no tenant selected. Queries
 *  should gate on it (`enabled: tenantId !== null`). */
export type TenantKey = string | null;

export const expenseKeys = {
  all: (t: TenantKey) => ['expenses', t] as const,
  lists: (t: TenantKey) => ['expenses', t, 'list'] as const,
  list: (t: TenantKey, query: unknown) => ['expenses', t, 'list', query] as const,
  details: (t: TenantKey) => ['expenses', t, 'detail'] as const,
  detail: (t: TenantKey, id: string) => ['expenses', t, 'detail', id] as const,
};

export const supplierKeys = {
  all: (t: TenantKey) => ['suppliers', t] as const,
  list: (t: TenantKey, query: unknown) => ['suppliers', t, 'list', query] as const,
  detail: (t: TenantKey, id: string) => ['suppliers', t, 'detail', id] as const,
};

export const expenseCategoryKeys = {
  all: (t: TenantKey) => ['expense-categories', t] as const,
  list: (t: TenantKey, query: unknown) => ['expense-categories', t, 'list', query] as const,
  detail: (t: TenantKey, id: string) => ['expense-categories', t, 'detail', id] as const,
};

export const expenseAttachmentKeys = {
  all: (t: TenantKey) => ['expense-attachments', t] as const,
  forExpense: (t: TenantKey, expenseId: string) =>
    ['expense-attachments', t, expenseId] as const,
};

export const expenseCommentKeys = {
  forExpense: (t: TenantKey, expenseId: string) =>
    ['expense-comments', t, expenseId] as const,
};

export const durcharbeiteKeys = {
  all: (t: TenantKey) => ['durcharbeite', t] as const,
  queue: (t: TenantKey, query: unknown) => ['durcharbeite', t, 'queue', query] as const,
  queues: (t: TenantKey) => ['durcharbeite', t, 'queue'] as const,
  item: (t: TenantKey, id: string) => ['durcharbeite', t, 'item', id] as const,
  items: (t: TenantKey) => ['durcharbeite', t, 'item'] as const,
};

export const exportJobKeys = {
  all: (t: TenantKey) => ['export-jobs', t] as const,
};

export const integrationKeys = {
  imapStatus: (t: TenantKey) => ['imap-status', t] as const,
  imapConfig: (t: TenantKey) => ['imap-config', t] as const,
  driveStatus: (t: TenantKey) => ['drive-status', t] as const,
  driveConfig: (t: TenantKey) => ['drive-config', t] as const,
};

export const quartalKeys = {
  all: (t: TenantKey) => ['quartal-status', t] as const,
  year: (t: TenantKey, year: number) => ['quartal-status', t, year] as const,
};

export const depreciationKeys = {
  all: (t: TenantKey) => ['depreciations', t] as const,
};

export const notificationKeys = {
  all: (t: TenantKey) => ['notifications', t] as const,
  list: (t: TenantKey, onlyUnread: boolean) => ['notifications', t, onlyUnread] as const,
};

export const expenseStatsKeys = {
  all: (t: TenantKey) => ['expense-stats', t] as const,
  quartal: (t: TenantKey, quartal: string) => ['expense-stats', t, quartal] as const,
};

export const uploadKeys = {
  processingStatus: (t: TenantKey, expenseId: string) =>
    ['expense-processing-status', t, expenseId] as const,
};

export const ocrDebugKeys = {
  detail: (t: TenantKey, expenseId: string) => ['ocr-debug', t, expenseId] as const,
};
